import { r as registerInstance, h, H as Host, g as getElement } from './index-662ec80e.js';
import { p as propCheck, r as runWatchers, h as hasPropError, d as displayPropError } from './propCheck-e7571e3e.js';
import { a as vString, W as vArrayOf, Z as vObjectWith, J as vNumber, K as vMinNumber, a3 as vDateObject, a1 as vUndefOr } from './validation-02b8d01d.js';
import { c as currentLocale, e as currencyFormat, n as numberFormat, f as dateFormat } from './i18n-939e2dee.js';
import { c as create, l as linear } from './linear-ce2d6534.js';
import { u as utcTime, a as area } from './area-9ced3c1f.js';
import { e as extent, m as min, c as curveLinear, l as line } from './line-3b16d424.js';
import { m as max } from './max-454095d8.js';
import { c as axisRight, a as axisBottom, b as axisLeft } from './axis-6ab3fed8.js';
import './array-d5d5138c.js';

const dsLineOverTimeCss = "ds-line-over-time{display:block}ds-line-over-time svg text{font-size:0.79012346rem;line-height:1.3rem;font-family:var(--font-family);color:var(--grey-900)}";

const DsLineOverTime = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    // px margins for labels overflowing chart area
    // all are minimums for allowing the axis labels to display
    // we use the span with formatCurrency to calc the left margin
    this.margin = { top: 8, right: 36, bottom: 80, left: null };
    this.propError = {};
    this.currentLocale = currentLocale();
    this.width = '100%';
    this.height = '100%';
    this.points = undefined;
    this.currency = undefined;
    this.color = 'var(--primary)';
    this.fill = 'var(--primary)';
    this.widthPx = undefined;
    this.heightPx = undefined;
  }
  // istanbul ignore next
  updateLocale() {
    this.currentLocale = currentLocale();
  }
  async watchWidth() {
    /* istanbul ignore if */
    if (!propCheck(this, 'width', 'string', vString))
      return;
  }
  async watchHeight() {
    /* istanbul ignore if */
    if (!propCheck(this, 'height', 'string', vString))
      return;
  }
  watchPoints() {
    /* istanbul ignore if */
    if (!propCheck(this, 'points', `Array<{ value: number, date: Date }>`, vArrayOf(vObjectWith({
      value: v => vNumber(v) || vMinNumber(0)(v),
      date: vDateObject
    }))))
      return;
  }
  watchCurrency() {
    /* istanbul ignore if */
    if (!propCheck(this, 'currency', 'string | undefined', vUndefOr(vString)))
      return;
  }
  async watchColor() {
    /* istanbul ignore if */
    if (!propCheck(this, 'color', 'string', vString))
      return;
  }
  async watchFill() {
    /* istanbul ignore if */
    if (!propCheck(this, 'fill', 'string', vString))
      return;
  }
  formatQuantity(quantity) {
    if (this.currency)
      return currencyFormat(quantity, this.currency, true);
    return numberFormat(quantity, 2);
  }
  getBoundingBox() {
    let { width, height } = this.host.getBoundingClientRect();
    width = Math.round(width);
    height = Math.round(height);
    let stateChanged = false;
    if (this.widthPx !== width) {
      this.widthPx = width;
      stateChanged = true;
    }
    if (this.heightPx !== height) {
      this.heightPx = height;
      stateChanged = true;
    }
    return stateChanged;
  }
  componentDidLoad() {
    this.resizeObserver = new ResizeObserver(() => {
      this.getBoundingBox();
    });
    this.resizeObserver.observe(this.host);
  }
  connectedCallback() {
    runWatchers(this);
    this.updateLocale();
  }
  render() {
    if (hasPropError(this))
      return displayPropError(this);
    return (h(Host, { style: { width: this.width, height: this.height } }, h("span", { ref: el => this.marginEl = el, class: "_text:xs", style: { 'font-family': 'var(--font-family)', 'visibility': 'hidden', 'position': 'absolute' } }, this.formatQuantity(Math.max(...this.points.map(p => p.value)) * 10))));
  }
  componentDidRender() {
    if (hasPropError(this))
      return; // something went wrong, bail
    if (this.getBoundingBox())
      return; // state change, another render is coming
    const { width } = this.marginEl.getBoundingClientRect();
    this.margin.left = Math.max(width + 8, this.margin.right);
    const svgElement = this.host.querySelector('svg');
    /* istanbul ignore if */
    if (svgElement)
      this.host.removeChild(svgElement);
    const svg = create('svg')
      .attr('viewBox', `0, 0, ${this.widthPx}, ${this.heightPx}`);
    const fillGradient = svg.append('defs').append('linearGradient')
      .attr('id', 'fillGradient')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', 0)
      .attr('y2', 1);
    fillGradient.append('stop')
      .attr('stop-color', this.fill)
      .attr('offset', '0%')
      .attr('stop-opacity', 0.2);
    fillGradient.append('stop')
      .attr('stop-color', 'white')
      .attr('offset', '100%')
      .attr('stop-opacity', 0.3);
    // x axis bounding and position
    const x = utcTime()
      .domain(extent(this.points, d => d.date))
      .range([this.margin.left, this.widthPx - this.margin.right]);
    // y axis bounding and position
    const y = linear()
      .domain([min(this.points, d => d.value), max(this.points, d => d.value)]).nice()
      .range([this.heightPx - this.margin.bottom, this.margin.top]);
    // shading area, place this under everything else
    const shading = area()
      .curve(curveLinear)
      .x(d => x(d[0]))
      .y0(this.heightPx - this.margin.bottom)
      .y1(d => y(d[1]));
    svg.append('path')
      .datum(this.points)
      .attr('fill', 'url(#fillGradient)')
      .attr('d', shading(this.points.map(r => [r.date.valueOf(), r.value])));
    // plot horizontal lines
    svg.append('g').call(g => g
      .attr("transform", `translate(${this.margin.left},0)`)
      .call(axisRight(y)
      .tickSize(this.widthPx - this.margin.left - this.margin.right)
      .tickFormat(() => ''))
      .call(g => g.select(".domain").remove())
      .call(g => g.selectAll(".tick line")
      .attr("stroke-opacity", 0.3)
      .attr("stroke-dasharray", "1,2")));
    // plot vertical lines
    svg.append('g').call(g => g
      .attr("transform", `translate(0,${this.margin.top})`)
      .call(axisBottom(x)
      .tickSize(this.heightPx - this.margin.top - this.margin.bottom)
      .tickFormat(() => ''))
      .call(g => g.select(".domain").remove())
      .call(g => g.selectAll(".tick line")
      .attr("stroke-opacity", 0.3)
      .attr("stroke-dasharray", "1,2")));
    // plot x axis
    svg.append('g').call(g => {
      g.attr('transform', `translate(0,${this.heightPx - this.margin.bottom})`)
        .call(axisBottom(x)
        .ticks(6)
        .tickSizeOuter(0)
        .tickFormat((v) => dateFormat(v, true)))
        .selectAll('path, .tick line').attr('stroke', 'var(--grey-800)');
      // rotate labels
      g.selectAll('text')
        .style("text-anchor", "end")
        .attr("dx", "-8px")
        .attr("dy", "-4px")
        .attr("transform", "rotate(-90)");
    });
    // plot y axis
    svg.append('g').call(g => g
      .attr('transform', `translate(${this.margin.left},0)`)
      .call(axisLeft(y)
      .tickFormat((v) => this.formatQuantity(v)))
      .selectAll('path, .tick line').attr('stroke', 'var(--grey-800)'));
    // plot line
    const plot = line()
      .defined(d => !isNaN(d[1]))
      .x(d => x(d[0]))
      .y(d => y(d[1]));
    svg.append('path')
      .datum(this.points)
      .attr('fill', 'none')
      .attr('stroke', this.color)
      .attr('stroke-width', 1.5)
      .attr('stroke-linejoin', 'round')
      .attr('stroke-linecap', 'round')
      .attr('d', plot(this.points.map(r => [r.date.valueOf(), r.value])));
    this.host.append(svg.node());
  }
  disconnectedCallback() {
    /* istanbul ignore else */
    if (this.resizeObserver)
      this.resizeObserver.disconnect();
  }
  get host() { return getElement(this); }
  static get watchers() { return {
    "width": ["watchWidth"],
    "height": ["watchHeight"],
    "points": ["watchPoints"],
    "currency": ["watchCurrency"],
    "color": ["watchColor"],
    "fill": ["watchFill"]
  }; }
};
DsLineOverTime.style = dsLineOverTimeCss;

export { DsLineOverTime as ds_line_over_time };
